/**
 * 掲示板 添付ファイル（成果物）の hooks。
 * 仕様書: docs/02_work/done/plan_board_feature.md §7-5
 *
 * - 一覧・登録・削除・ダウンロード URL 発行はすべて Callable 経由。
 *   Firestore の attachments サブコレクションは rules で read を塞いでいる
 *   （passwordHash を含むため素で読ませない）。
 * - ダウンロードは resolveBoardAttachmentDownload が返す 5 分間有効な署名付き URL。
 *   パスワード照合が通るまで URL は存在しない。
 */
import type {
  BoardAttachmentKind,
  BoardAttachmentView,
  BoardRecipientState,
} from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';

import { functions } from '@/lib/firebase';

const listFn = httpsCallable<{ postId: string }, { attachments: BoardAttachmentView[] }>(
  functions,
  'listBoardAttachments',
);

const registerFn = httpsCallable<
  {
    postId: string;
    storagePath: string;
    fileName: string;
    size: number;
    contentType?: string;
    state?: BoardRecipientState;
    kind?: BoardAttachmentKind;
    password?: string;
    replaceAttachmentId?: string;
  },
  { attachmentId: string }
>(functions, 'registerBoardAttachment');

const deleteFn = httpsCallable<{ postId: string; attachmentId: string }, { ok: true }>(
  functions,
  'deleteBoardAttachment',
);

export type AttachmentDownloadResult =
  | { status: 'ok'; downloadUrl: string; fileName: string; expiresAt: string }
  | { status: 'password_required' }
  | { status: 'wrong_password' }
  | { status: 'file_missing' };

const resolveDownloadFn = httpsCallable<
  { postId: string; attachmentId: string; password?: string },
  AttachmentDownloadResult
>(functions, 'resolveBoardAttachmentDownload');

/** 投稿の添付一覧。 */
export function useBoardAttachments(postId: string | undefined) {
  return useQuery({
    queryKey: ['board_attachments', postId],
    enabled: Boolean(postId),
    queryFn: async () => {
      const res = await listFn({ postId: postId! });
      return res.data.attachments;
    },
  });
}

/** アップロード済みファイルを添付として登録する。 */
export function useRegisterBoardAttachment(postId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      storagePath: string;
      fileName: string;
      size: number;
      contentType?: string;
      state?: BoardRecipientState;
      kind?: BoardAttachmentKind;
      password?: string;
      replaceAttachmentId?: string;
    }) => {
      const res = await registerFn({ postId: postId!, ...input });
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['board_attachments', postId] }),
  });
}

/** 添付を削除する（実体 + メタ）。 */
export function useDeleteBoardAttachment(postId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (attachmentId: string) => {
      const res = await deleteFn({ postId: postId!, attachmentId });
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['board_attachments', postId] }),
  });
}

/**
 * ダウンロード URL を取得する（パスワード照合込み・1 往復）。
 * 成功時のみ downloadUrl が返る。呼び出し側で即座にダウンロードを開始すること。
 */
export function useResolveAttachmentDownload(postId: string | undefined) {
  return useMutation({
    mutationFn: async (input: { attachmentId: string; password?: string }) => {
      const res = await resolveDownloadFn({ postId: postId!, ...input });
      return res.data;
    },
  });
}

/** 署名付き URL からダウンロードを開始する（ブラウザ標準の保存動作に委ねる）。 */
export function startDownload(url: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener noreferrer';
  // Content-Disposition: attachment がサーバ側で付くため download 属性は補助
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** バイト数を人間可読なサイズ表記にする。 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const v = bytes / Math.pow(1024, i);
  return `${v >= 10 || i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
}
